import React, { useContext, useMemo, useState } from 'react';
import styled from 'astroturf/react';
import { Heading } from 'js/design_system/Heading';
import { Body } from 'js/design_system/Body';
import { Icon } from 'js/design_system/Icon';
import { TextButton } from 'js/design_system/TextButton';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Spinner } from 'js/design_system/Spinner';
import { CiterightContext } from './CiterightContextProvider';
import type {
  ReferenceMetadataValidation,
  ReferenceEvaluationSummary,
} from './types';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TableCard = styled.div`
  background: var(--background-white, #ffffff);
  border-radius: 8px;
  border: 1px solid var(--stroke-light);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Thead = styled.thead`
  background: var(--background-beige-light);
  border-bottom: 1px solid var(--stroke-light);
`;

const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  white-space: nowrap;
`;

const ThNum = styled(Th)`
  width: 48px;
  text-align: right;
  padding-right: 8px;
`;

const ThStatus = styled(Th)`
  width: 40%;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid var(--stroke-light);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--background-beige-light);
  }
`;

const Td = styled.td`
  padding: 10px 12px;
  vertical-align: middle;
  word-break: break-word;
`;

const TdNum = styled(Td)`
  width: 48px;
  text-align: right;
  color: var(--text-secondary);
  font-size: 14px;
  white-space: nowrap;
  padding-right: 8px;
`;

const TdStatus = styled(Td)`
  width: 40%;
`;

const StatusCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PendingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: var(--background-white, #ffffff);
  border-radius: 8px;
  border: 1px solid var(--stroke-light);
`;

type StatusResult = {
  textBefore: string;
  linkText?: string;
  icon: string;
  color: string;
};

function getStatus(
  validation: ReferenceMetadataValidation | null,
  ref: ReferenceEvaluationSummary['references'][number],
): StatusResult {
  if (!validation)
    return {
      textBefore: 'Could not verify',
      icon: 'help',
      color: 'var(--text-secondary)',
    };
  if (validation.publication_matches === false) {
    const refPub = ref.publication;
    const foundPub = ref.found_metadata?.publication;
    const textBefore =
      refPub && foundPub
        ? `Publication mismatch: \u201c${refPub}\u201d in your reference vs \u201c${foundPub}\u201d in `
        : 'Publication source doesn\u2019t match ';
    return {
      textBefore,
      linkText: 'matched paper',
      icon: 'warning',
      color: 'var(--brand-colors-error-500)',
    };
  }
  if (validation.year_matches === false) {
    const refYear = ref.publication_year;
    const foundYear = ref.found_metadata?.publication_year;
    const textBefore =
      refYear != null && foundYear != null
        ? `Year mismatch: \u201c${refYear}\u201d in your reference vs \u201c${foundYear}\u201d in `
        : 'Year mismatch in ';
    return {
      textBefore,
      linkText: 'matched paper',
      icon: 'warning',
      color: 'var(--brand-colors-error-500)',
    };
  }
  if (validation.authors_match === false) {
    const refAuthors = ref.authors?.join(', ');
    const foundAuthors = ref.found_metadata?.authors?.join(', ');
    const textBefore =
      refAuthors && foundAuthors
        ? `Author mismatch: \u201c${refAuthors}\u201d in your reference vs \u201c${foundAuthors}\u201d in `
        : 'Authors don\u2019t match ';
    return {
      textBefore,
      linkText: 'matched paper',
      icon: 'warning',
      color: 'var(--brand-colors-error-500)',
    };
  }
  if (validation.title_similarity_threshold_met === false) {
    const refTitle = ref.title;
    const foundTitle = ref.found_metadata?.title;
    const textBefore =
      refTitle && foundTitle
        ? `Title mismatch: \u201c${refTitle}\u201d in your reference vs \u201c${foundTitle}\u201d in `
        : 'Title doesn\u2019t match ';
    return {
      textBefore,
      linkText: 'matched paper',
      icon: 'warning',
      color: 'var(--brand-colors-error-500)',
    };
  }
  if (validation.doi_format_valid === false)
    return {
      textBefore: 'Invalid DOI format',
      icon: 'warning',
      color: 'var(--brand-colors-error-500)',
    };
  if (validation.doi_resolves === false)
    return {
      textBefore: 'DOI link is broken',
      icon: 'warning',
      color: 'var(--brand-colors-error-500)',
    };
  return {
    textBefore: 'No issues detected',
    icon: 'check_circle',
    color: 'var(--brand-colors-success-500)',
  };
}

const RUNNING_STATUSES = new Set([
  'pending',
  'processing_document',
  'extracting_references',
  'validating_references',
  'extracting_claims',
  'evaluating_claims',
]);

type RefEntry = {
  ref: ReferenceEvaluationSummary['references'][number];
  refNumber: number;
  validation: ReferenceMetadataValidation | null;
};

type TableRowProps = {
  refNumber: number;
  reference: ReferenceEvaluationSummary['references'][number];
  validation: ReferenceMetadataValidation | null;
};

function getMatchedPaperUrl(
  reference: ReferenceEvaluationSummary['references'][number],
): string | null {
  const doi = reference.found_metadata?.doi;
  if (doi) return `https://doi.org/${doi}`;
  const url = reference.best_source_url;
  if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
    return url;
  }
  return null;
}

function TableRow({ refNumber, reference, validation }: TableRowProps) {
  const displayText = reference.raw_reference || 'Untitled';
  const status = getStatus(validation, reference);
  const matchedPaperUrl = status.linkText
    ? getMatchedPaperUrl(reference)
    : null;

  return (
    <Tr>
      <TdNum>{refNumber}</TdNum>
      <Td>
        <Body size="sm" style={{ lineHeight: '1.4' }}>
          {displayText}
        </Body>
      </Td>
      <TdStatus>
        <StatusCell>
          <Icon
            name={status.icon}
            size={16}
            style={{ color: status.color, flexShrink: 0 }}
          />
          <Body size="sm" color="secondary">
            {status.textBefore}
            {status.linkText &&
              (matchedPaperUrl ? (
                <BodyLink
                  size="sm"
                  href={matchedPaperUrl}
                  target="_blank"
                  external
                  underline
                  style={{ color: 'var(--text-link)' }}
                >
                  {status.linkText}
                  <Icon
                    name="open_in_new"
                    size={12}
                    style={{ verticalAlign: 'middle', marginLeft: 2 }}
                  />
                </BodyLink>
              ) : (
                status.linkText
              ))}
          </Body>
        </StatusCell>
      </TdStatus>
    </Tr>
  );
}

export const ReferenceReportPage: React.FC = () => {
  const context = useContext(CiterightContext);
  const [showAll, setShowAll] = useState(false);
  const refEval = context?.report?.reference_evaluation;

  const isRunning = refEval ? RUNNING_STATUSES.has(refEval.status) : false;
  const isCompleted = refEval?.status === 'completed';

  const { allValidatedRefs, warningRefs, unmatchedRefs } = useMemo(() => {
    const all: RefEntry[] = [];
    const warning: RefEntry[] = [];
    const unmatched: RefEntry[] = [];
    (refEval?.references || [])
      .filter((ref) => ref.title)
      .forEach((ref, i) => {
        const entry: RefEntry = {
          ref,
          refNumber: i + 1,
          validation: ref.validation ?? null,
        };
        if (!ref.validation) {
          unmatched.push(entry);
          return;
        }
        all.push(entry);
        if (getStatus(entry.validation, entry.ref).icon !== 'check_circle') {
          warning.push(entry);
        }
      });
    return {
      allValidatedRefs: all,
      warningRefs: warning,
      unmatchedRefs: unmatched,
    };
  }, [refEval?.references]);

  const totalCount = refEval?.references?.length ?? 0;

  if (totalCount === 0 && !refEval) {
    return (
      <PageContainer>
        <Heading size="sm" as="h2">
          Reference Report
        </Heading>
        <Body size="sm" color="secondary">
          No references found. Upload a document with a references section to
          see metadata validation results.
        </Body>
      </PageContainer>
    );
  }

  const hiddenCount = allValidatedRefs.length - warningRefs.length;
  const matchedCount = allValidatedRefs.length;
  const needAttentionCount = warningRefs.length;
  const unmatchedCount = totalCount - allValidatedRefs.length;

  return (
    <PageContainer>
      <Section>
        <Heading size="sm" as="h2">
          Overview
        </Heading>

        <div
          style={{
            background: 'var(--background-white, #ffffff)',
            border: '1px solid var(--stroke-light)',
            borderRadius: '8px',
            padding: '16px',
          }}
        >
          {isRunning && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Spinner size={16} />
              <Body size="sm" color="secondary">
                Checking reference metadata…
              </Body>
            </div>
          )}
          {isCompleted && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              <Body size="sm">
                We found {totalCount} reference
                {totalCount === 1 ? '' : 's'} in your document:
              </Body>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Icon
                  name="check_circle"
                  size={16}
                  style={{
                    color: 'var(--brand-colors-success-500)',
                    flexShrink: 0,
                  }}
                />
                <Body size="sm">
                  {matchedCount} reference{matchedCount === 1 ? '' : 's'}{' '}
                  matched to a known publication record
                </Body>
              </div>
              {unmatchedCount > 0 && (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Icon
                    name="help"
                    size={16}
                    style={{
                      color: 'var(--text-secondary)',
                      flexShrink: 0,
                    }}
                  />
                  <Body size="sm" color="secondary">
                    {unmatchedCount} reference
                    {unmatchedCount === 1 ? '' : 's'} could not be matched
                  </Body>
                </div>
              )}
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Icon
                  name="warning"
                  size={16}
                  style={{
                    color: 'var(--brand-colors-error-500)',
                    flexShrink: 0,
                  }}
                />
                <Body size="sm">
                  {needAttentionCount} reference
                  {needAttentionCount === 1 ? '' : 's'} need attention
                </Body>
              </div>
            </div>
          )}
        </div>
      </Section>

      <Section>
        <Heading size="sm" as="h2">
          Reference Report
        </Heading>

        {isRunning && (
          <PendingContainer>
            <Spinner size={16} />
            <Body size="sm" color="secondary">
              Validating references against OpenAlex…
            </Body>
          </PendingContainer>
        )}

        {isCompleted && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Body size="sm" color="secondary">
              {warningRefs.length > 0
                ? `We found potential issues with ${warningRefs.length} reference${warningRefs.length === 1 ? '' : 's'}.`
                : 'No issues detected across all references.'}
            </Body>
            <Body size="sm" color="secondary">
              This check may occasionally flag correct references. We look for
              possible mismatches in title, author, year, and source details.
            </Body>
          </div>
        )}

        {(warningRefs.length > 0 || unmatchedRefs.length > 0 || showAll) && (
          <TableCard>
            <Table>
              <Thead>
                <tr>
                  <ThNum>#</ThNum>
                  <Th style={{ width: '60%' }}>Reference</Th>
                  <ThStatus>Status</ThStatus>
                </tr>
              </Thead>
              <Tbody>
                {[
                  ...(showAll ? allValidatedRefs : warningRefs),
                  ...(showAll ? unmatchedRefs : []),
                ]
                  .sort((a, b) => a.refNumber - b.refNumber)
                  .map(({ ref, refNumber, validation }) => (
                    <TableRow
                      key={ref.id || refNumber}
                      refNumber={refNumber}
                      reference={ref}
                      validation={validation}
                    />
                  ))}
              </Tbody>
            </Table>
          </TableCard>
        )}
        {!showAll && (hiddenCount > 0 || unmatchedRefs.length > 0) && (
          <TextButton
            size="sm"
            onClick={() => setShowAll(true)}
            style={{ paddingLeft: 0, alignSelf: 'flex-start' }}
          >
            Show all reference checks
          </TextButton>
        )}
        {showAll && (hiddenCount > 0 || unmatchedRefs.length > 0) && (
          <TextButton
            size="sm"
            onClick={() => setShowAll(false)}
            style={{ paddingLeft: 0, alignSelf: 'flex-start' }}
          >
            Show only issues
          </TextButton>
        )}
      </Section>
    </PageContainer>
  );
};

export default ReferenceReportPage;
